import axios, { AxiosInstance } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  ConfirmEmailRequest,
  ResendVerificationEmailRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UserResponse,
  AppointmentResponse,
  BookAppointmentRequest,
  CreateClientRequest,
  UpdateClientRequest,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  SubscriptionHistoryItem,
  GlobalSetting,
  EmailTemplate,
  MonthlyLimitOverrideRequest,
  TimeslotInfo,
  UpdateTimeslotRequest,
  TimeslotSchedule,
  TimeslotCapacityResponse,
  TimeslotCapacityInfo,
  ExpressInterestRequest,
  TimeslotInterestResponse,
  UpdateTimeslotCapacityRequest,
  UpdateTimeslotTimeRequest,
  DisableTimeslotRequest,
  DisableDayRequest,
  DisableDateRangeRequest,
  TimeslotOverrideResponse,
  TimeslotClientsResponse,
  TimeslotClientsBatchResponse,
  GlobalTimeslotDefinitionResponse,
  CreateGlobalTimeslotRequest,
  UpdateGlobalTimeslotTimeRequest,
  UpdateGlobalTimeslotCapacityRequest,
  UpdateGlobalTimeslotEnabledRequest,
  BulkUpdateGlobalTimeslotsRequest,
  DayOfWeek,
  BroadcastEmailRequest,
  BroadcastEmailResponse,
  EmailAttachmentResponse,
  UpdateLanguagePreferenceRequest,
  CancellationDeadlineSettingsResponse,
  BodyFatMeasurementResponse,
  CreateBodyFatMeasurementRequest,
  UpdateBodyFatMeasurementRequest
} from '../types/api';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  // Helper method to get admin token from redundant storage
  private getAdminToken(): string | null {
    // Check all storage locations, return first found
    
    // Check cookies
    const cookieChecks = ['gymAuth1=', 'sessionData=', 'appToken='];
    for (const cookiePrefix of cookieChecks) {
      const value = document.cookie
        .split('; ')
        .find(row => row.startsWith(cookiePrefix))
        ?.split('=')[1];
      if (value) return value;
    }
    
    // Check localStorage
    const localStorageValue = localStorage.getItem('gymAppState') || localStorage.getItem('adminBackup');
    if (localStorageValue) return localStorageValue;
    
    // Check sessionStorage
    const sessionStorageValue = sessionStorage.getItem('userSession');
    if (sessionStorageValue) return sessionStorageValue;
    
    return null;
  }

  // Helper method to delete admin tokens
  private deleteAdminToken(): void {
    // Clear all storage locations
    document.cookie = 'gymAuth1=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'sessionData=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'appToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    localStorage.removeItem('gymAppState');
    localStorage.removeItem('adminBackup');
    sessionStorage.removeItem('userSession');
    
    try {
      const request = indexedDB.open('gymAuthDB', 1);
      request.onsuccess = (event: any) => {
        const db = event.target.result;
        const transaction = db.transaction(['auth'], 'readwrite');
        const store = transaction.objectStore('auth');
        store.delete('adminToken');
        store.delete('backupToken');
      };
    } catch (error) {
      console.error('IndexedDB deletion failed:', error);
    }
  }

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      // Get current user to determine which token to use
      const userData = localStorage.getItem('user');
      let token: string | null = null;
      
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          if (parsedUser.role === 1) { // UserRole.Admin
            // Current user is admin - use redundant token storage
            token = this.getAdminToken();
          } else {
            // Current user is client - use localStorage
            token = localStorage.getItem('authToken');
          }
        } catch (error) {
          // If user data is corrupted, don't send any token
          console.error('Error parsing user data for token selection:', error);
        }
      }
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't redirect on login endpoint failures - let the login component handle it
        const isLoginEndpoint = error.config?.url?.includes('/api/auth/login');
        
        if (error.response?.status === 401 && !isLoginEndpoint) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          this.deleteAdminToken();
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper function to convert time string to TimeSpan
  private timeStringToTimeSpan(timeString: string) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const ticks = totalMinutes * 60 * 10000000; // Convert to .NET ticks
    
    return {
      ticks,
      days: 0,
      hours,
      minutes,
      seconds: 0,
      milliseconds: 0,
      totalDays: 0,
      totalHours: hours,
      totalMinutes: totalMinutes,
      totalSeconds: totalMinutes * 60,
      totalMilliseconds: totalMinutes * 60 * 1000
    };
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await this.api.post('/api/auth/login', credentials);
    return response.data;
  }

  async register(registrationData: RegisterRequest): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/register', registrationData);
    return response.data;
  }

  async confirmEmail(confirmationData: ConfirmEmailRequest): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/confirm-email', confirmationData);
    return response.data;
  }

  async resendVerificationEmail(resendData: ResendVerificationEmailRequest): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/resend-verification-email', resendData);
    return response.data;
  }

  async forgotPassword(forgotPasswordData: ForgotPasswordRequest): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/forgot-password', forgotPasswordData);
    return response.data;
  }

  async resetPassword(resetPasswordData: ResetPasswordRequest): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/reset-password', resetPasswordData);
    return response.data;
  }

  // Client endpoints
  async getProfile(): Promise<ApiResponse<UserResponse>> {
    const response = await this.api.get('/api/Client/profile');
    return response.data;
  }

  async getAppointments(startDate?: string, endDate?: string): Promise<ApiResponse<AppointmentResponse[]>> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await this.api.get(`/api/Client/appointments?${params.toString()}`);
    return response.data;
  }

  async bookAppointment(appointment: BookAppointmentRequest): Promise<ApiResponse<AppointmentResponse>> {
    const response = await this.api.post('/api/Client/appointments', appointment);
    return response.data;
  }

  async cancelAppointment(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/Client/appointments/${id}`);
    return response.data;
  }

  async checkTimeSlotAvailability(date: string, startTime: string, endTime: string): Promise<ApiResponse<boolean>> {
    const params = new URLSearchParams();
    params.append('date', date);
    params.append('startTime', startTime);
    params.append('endTime', endTime);
    
    const response = await this.api.get(`/api/Client/timeslots/available?${params.toString()}`);
    return response.data;
  }

  // Admin endpoints
  async getClients(search?: string): Promise<ApiResponse<UserResponse[]>> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const response = await this.api.get(`/api/Admin/clients${queryString ? `?${queryString}` : ''}`);
    return response.data;
  }

  async createClient(client: CreateClientRequest): Promise<ApiResponse<UserResponse>> {
    const response = await this.api.post('/api/Admin/clients', client);
    return response.data;
  }

  async updateClient(id: number, client: UpdateClientRequest): Promise<ApiResponse<UserResponse>> {
    const response = await this.api.put(`/api/Admin/clients/${id}`, client);
    return response.data;
  }

  async deleteClient(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/Admin/clients/${id}`);
    return response.data;
  }

  async getClientById(id: number): Promise<ApiResponse<UserResponse>> {
    const response = await this.api.get(`/api/Admin/clients/${id}`);
    return response.data;
  }

  async createClientSubscription(id: number, subscription: CreateSubscriptionRequest): Promise<ApiResponse> {
    const response = await this.api.post(`/api/Admin/clients/${id}/subscription`, subscription);
    return response.data;
  }

  async updateClientSubscription(id: number, subscription: UpdateSubscriptionRequest): Promise<ApiResponse> {
    const response = await this.api.put(`/api/Admin/clients/${id}/subscription`, subscription);
    return response.data;
  }

  async updateClientMonthlyLimit(id: number, monthlyLimit: MonthlyLimitOverrideRequest): Promise<ApiResponse> {
    const response = await this.api.put(`/api/Admin/clients/${id}/monthly-limit`, monthlyLimit);
    return response.data;
  }

  async freezeClientSubscription(id: number): Promise<ApiResponse> {
    const response = await this.api.post(`/api/Admin/subscriptions/${id}/freeze`);
    return response.data;
  }

  async unfreezeClientSubscription(id: number): Promise<ApiResponse> {
    const response = await this.api.post(`/api/Admin/subscriptions/${id}/unfreeze`);
    return response.data;
  }

  async getClientSubscriptionHistory(clientId: number): Promise<ApiResponse<SubscriptionHistoryItem[]>> {
    const response = await this.api.get(`/api/admin/clients/${clientId}/subscriptions`);
    return response.data;
  }

  async getSubscriptionHistory(): Promise<ApiResponse<SubscriptionHistoryItem[]>> {
    const response = await this.api.get('/api/client/subscriptions');
    return response.data;
  }

  async updateClientAdminNotification(id: number, enabled: boolean): Promise<ApiResponse> {
    const response = await this.api.put(`/api/admin/clients/${id}/admin-notification`, enabled);
    return response.data;
  }

  async getClientAppointments(clientId: number, startDate?: string, endDate?: string): Promise<ApiResponse<AppointmentResponse[]>> {
    const params = new URLSearchParams();
    params.append('userId', clientId.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await this.api.get(`/api/admin/appointments?${params.toString()}`);
    return response.data;
  }

  async bookAppointmentAsAdmin(appointment: BookAppointmentRequest): Promise<ApiResponse<AppointmentResponse>> {
    const response = await this.api.post('/api/Admin/appointments', appointment);
    return response.data;
  }

  async cancelAppointmentAsAdmin(id: number, countTowardLimit: boolean = false): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/Admin/appointments/${id}?countTowardLimit=${countTowardLimit}`);
    return response.data;
  }

  async permanentDeleteAppointment(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/Admin/appointments/${id}/permanent`);
    return response.data;
  }

  async getEmailTemplates(): Promise<ApiResponse<EmailTemplate[]>> {
    const response = await this.api.get('/api/Admin/email-templates');
    return response.data;
  }

  async updateEmailTemplate(id: number, template: EmailTemplate): Promise<ApiResponse> {
    const response = await this.api.put(`/api/Admin/email-templates/${id}`, template);
    return response.data;
  }

  async getSettings(): Promise<ApiResponse<GlobalSetting[]>> {
    const response = await this.api.get('/api/Admin/settings');
    return response.data;
  }

  async updateSetting(id: number, setting: { value: string }): Promise<ApiResponse> {
    const response = await this.api.put(`/api/Admin/settings/${id}`, setting);
    return response.data;
  }

  // Admin Timeslot Management endpoints
  async updateTimeslotCapacity(request: UpdateTimeslotCapacityRequest): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const response = await this.api.put('/api/Admin/timeslots/capacity', request);
    return response.data;
  }

  async updateTimeslotTime(request: UpdateTimeslotTimeRequest): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const response = await this.api.put('/api/Admin/timeslots/time', request);
    return response.data;
  }

  async disableTimeslot(request: DisableTimeslotRequest): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const response = await this.api.post('/api/Admin/timeslots/disable', request);
    return response.data;
  }

  async disableDay(request: DisableDayRequest): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const response = await this.api.post('/api/Admin/timeslots/disable-day', request);
    return response.data;
  }

  async disableDateRange(request: DisableDateRangeRequest): Promise<ApiResponse<TimeslotOverrideResponse[]>> {
    const response = await this.api.post('/api/Admin/timeslots/disable-range', request);
    return response.data;
  }

  async getTimeslotOverrides(date?: string, startDate?: string, endDate?: string): Promise<ApiResponse<TimeslotOverrideResponse[]>> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await this.api.get(`/api/Admin/timeslots/overrides?${params.toString()}`);
    return response.data;
  }

  async deleteTimeslotOverride(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/Admin/timeslots/overrides/${id}`);
    return response.data;
  }

  // Helper methods for timeslot management
  async updateTimeslotCapacitySimple(date: string, startTime: string, endTime: string, newCapacity: number, reason?: string): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const request: UpdateTimeslotCapacityRequest = {
      date,
      startTime: this.timeStringToTimeSpan(startTime),
      endTime: this.timeStringToTimeSpan(endTime),
      newCapacity,
      reason
    };
    return this.updateTimeslotCapacity(request);
  }

  async updateTimeslotTimeSimple(date: string, originalStartTime: string, originalEndTime: string, newStartTime: string, newEndTime: string, reason?: string): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const request: UpdateTimeslotTimeRequest = {
      date,
      originalStartTime: this.timeStringToTimeSpan(originalStartTime),
      originalEndTime: this.timeStringToTimeSpan(originalEndTime),
      newStartTime: this.timeStringToTimeSpan(newStartTime),
      newEndTime: this.timeStringToTimeSpan(newEndTime),
      reason
    };
    return this.updateTimeslotTime(request);
  }

  async disableTimeslotSimple(date: string, startTime: string, endTime: string, reason?: string): Promise<ApiResponse<TimeslotOverrideResponse>> {
    const request: DisableTimeslotRequest = {
      date,
      startTime: this.timeStringToTimeSpan(startTime),
      endTime: this.timeStringToTimeSpan(endTime),
      reason
    };
    return this.disableTimeslot(request);
  }

  // Timeslot management endpoints
  async getTimeslots(date: string): Promise<ApiResponse<{ timeslots: TimeslotInfo[], isFullyDisabled?: boolean, disabledReason?: string }>> {
    const response = await this.api.get(`/api/Timeslot/capacity?date=${date}`);
    const capacityData: ApiResponse<TimeslotCapacityResponse> = response.data;
    
    if (capacityData.success && capacityData.data) {
      // Transform the capacity response to TimeslotInfo format
      const transformedTimeslots: TimeslotInfo[] = capacityData.data.timeslots.map((slot: TimeslotCapacityInfo, index: number) => ({
        id: index + 1, // Generate a temporary ID since it's not provided
        date: capacityData.data!.date,
        originalStartTime: slot.originalStartTime,
        originalEndTime: slot.originalEndTime,
        startTime: slot.startTime,
        endTime: slot.endTime,
        capacity: slot.totalCapacity,
        currentBookings: slot.currentBookings,
        isAvailable: slot.isAvailable,
        isGymOpen: slot.isGymOpen,
        isDisabled: slot.isDisabled,
        disabledReason: slot.disabledReason,
        appointments: [], // Empty since we don't get appointment details in this response
        hasOverride: slot.hasOverride,
        hasTimeChange: slot.hasTimeChange,
        hasCapacityOverride: slot.hasCapacityOverride,
        overrideReason: slot.overrideReason
      }));
      
      return {
        success: true,
        message: capacityData.message,
        data: {
          timeslots: transformedTimeslots,
          isFullyDisabled: capacityData.data.isFullyDisabled,
          disabledReason: capacityData.data.disabledReason
        },
        errors: capacityData.errors
      };
    }
    
    return {
      success: false,
      message: capacityData.message || 'Failed to load timeslots',
      data: { timeslots: [], isFullyDisabled: false },
      errors: capacityData.errors
    };
  }

  async updateTimeslot(timeslotId: number, update: UpdateTimeslotRequest): Promise<ApiResponse> {
    const response = await this.api.put(`/api/Timeslot/${timeslotId}`, update);
    return response.data;
  }

  async getTimeslotSchedule(): Promise<ApiResponse<TimeslotSchedule[]>> {
    const response = await this.api.get('/api/Timeslot/schedule');
    return response.data;
  }

  async updateTimeslotSchedule(schedule: TimeslotSchedule[]): Promise<ApiResponse> {
    const response = await this.api.put('/api/Timeslot/schedule', schedule);
    return response.data;
  }

  // Timeslot endpoints
  async validateTimeslot(date: string, startTime: string, endTime: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    params.append('date', date);
    params.append('startTime', startTime);
    params.append('endTime', endTime);
    
    const response = await this.api.get(`/api/Timeslot/validate?${params.toString()}`);
    return response.data;
  }

  async getSchedule(): Promise<ApiResponse> {
    const response = await this.api.get(`/api/Timeslot/schedule`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.api.get('/api/health');
    return response.data;
  }

  // Timeslot Interest endpoints
  async expressInterest(interest: ExpressInterestRequest): Promise<ApiResponse<TimeslotInterestResponse>> {
    const response = await this.api.post('/api/TimeslotInterest/express', interest);
    return response.data;
  }

  async removeInterest(interestId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/TimeslotInterest/${interestId}`);
    return response.data;
  }

  async getMyInterests(): Promise<ApiResponse<TimeslotInterestResponse[]>> {
    const response = await this.api.get('/api/TimeslotInterest/my-interests');
    return response.data;
  }

  // Get booked clients for a specific timeslot
  async getTimeslotClients(date: string, startTime: string, endTime: string): Promise<ApiResponse<TimeslotClientsResponse>> {
    const params = new URLSearchParams();
    params.append('date', date);
    params.append('startTime', startTime);
    params.append('endTime', endTime);
    
    const response = await this.api.get(`/api/Admin/timeslots/clients?${params.toString()}`);
    return response.data;
  }

  // Get booked clients for every timeslot on a date in one call (replaces the per-timeslot fan-out)
  async getTimeslotClientsBatch(date: string): Promise<ApiResponse<TimeslotClientsBatchResponse>> {
    const response = await this.api.get(`/api/Admin/timeslots/clients-batch?date=${date}`);
    return response.data;
  }

  // Global Timeslot Management endpoints
  async getGlobalTimeslots(dayOfWeek?: DayOfWeek): Promise<ApiResponse<GlobalTimeslotDefinitionResponse[]>> {
    const params = dayOfWeek !== undefined ? `?dayOfWeek=${dayOfWeek}` : '';
    const response = await this.api.get(`/api/Admin/global-timeslots${params}`);
    return response.data;
  }

  async createGlobalTimeslot(request: CreateGlobalTimeslotRequest): Promise<ApiResponse<GlobalTimeslotDefinitionResponse>> {
    const response = await this.api.post('/api/Admin/global-timeslots', request);
    return response.data;
  }

  async updateGlobalTimeslotTime(originalStart: string, originalEnd: string, request: UpdateGlobalTimeslotTimeRequest): Promise<ApiResponse<GlobalTimeslotDefinitionResponse>> {
    const response = await this.api.put(`/api/Admin/global-timeslots/${originalStart}/${originalEnd}/time`, request);
    return response.data;
  }

  async updateGlobalTimeslotCapacity(originalStart: string, originalEnd: string, request: UpdateGlobalTimeslotCapacityRequest): Promise<ApiResponse<GlobalTimeslotDefinitionResponse>> {
    const response = await this.api.put(`/api/Admin/global-timeslots/${originalStart}/${originalEnd}/capacity`, request);
    return response.data;
  }

  async updateGlobalTimeslotEnabled(originalStart: string, originalEnd: string, request: UpdateGlobalTimeslotEnabledRequest): Promise<ApiResponse<GlobalTimeslotDefinitionResponse>> {
    const response = await this.api.put(`/api/Admin/global-timeslots/${originalStart}/${originalEnd}/enabled`, request);
    return response.data;
  }

  async deleteGlobalTimeslot(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/Admin/global-timeslots/${id}`);
    return response.data;
  }

  async resetGlobalTimeslot(originalStart: string, originalEnd: string, dayOfWeek?: DayOfWeek): Promise<ApiResponse<GlobalTimeslotDefinitionResponse>> {
    const params = dayOfWeek !== undefined ? `?dayOfWeek=${dayOfWeek}` : '';
    const response = await this.api.post(`/api/Admin/global-timeslots/${originalStart}/${originalEnd}/reset${params}`);
    return response.data;
  }

  async bulkUpdateGlobalTimeslots(request: BulkUpdateGlobalTimeslotsRequest): Promise<ApiResponse<GlobalTimeslotDefinitionResponse[]>> {
    const response = await this.api.put('/api/Admin/global-timeslots/bulk', request);
    return response.data;
  }

  // Broadcast Email endpoint
  async sendBroadcastEmail(request: BroadcastEmailRequest): Promise<BroadcastEmailResponse> {
    const response = await this.api.post('/api/admin/send-broadcast-email', request);
    return response.data;
  }

  async uploadBroadcastAttachments(files: File[]): Promise<ApiResponse<EmailAttachmentResponse[]>> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await this.api.post('/api/admin/broadcast-attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async deleteBroadcastAttachment(id: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/admin/broadcast-attachments/${id}`);
    return response.data;
  }

  // Language Preference endpoints
  async getLanguagePreference(isAdmin: boolean = false): Promise<ApiResponse<string>> {
    const endpoint = isAdmin ? '/api/Admin/language-preference' : '/api/Client/language-preference';
    const response = await this.api.get(endpoint);
    return response.data;
  }

  async updateLanguagePreference(request: UpdateLanguagePreferenceRequest, isAdmin: boolean = false): Promise<ApiResponse> {
    const endpoint = isAdmin ? '/api/Admin/language-preference' : '/api/Client/language-preference';
    const response = await this.api.put(endpoint, request);
    return response.data;
  }

  // Cancellation Deadline Settings endpoint
  async getCancellationDeadlineSettings(): Promise<ApiResponse<CancellationDeadlineSettingsResponse>> {
    const response = await this.api.get('/api/client/cancellation-deadline-settings');
    return response.data;
  }

  // Body Fat Measurement - Admin endpoints
  async getClientBodyFatMeasurements(clientId: number): Promise<ApiResponse<BodyFatMeasurementResponse[]>> {
    const response = await this.api.get(`/api/admin/clients/${clientId}/body-fat-measurements`);
    return response.data;
  }

  async getClientLatestBodyFatMeasurement(clientId: number): Promise<ApiResponse<BodyFatMeasurementResponse>> {
    const response = await this.api.get(`/api/admin/clients/${clientId}/body-fat-measurements/latest`);
    return response.data;
  }

  async createBodyFatMeasurement(clientId: number, request: CreateBodyFatMeasurementRequest): Promise<ApiResponse<BodyFatMeasurementResponse>> {
    const response = await this.api.post(`/api/admin/clients/${clientId}/body-fat-measurements`, request);
    return response.data;
  }

  async updateBodyFatMeasurement(clientId: number, measurementId: number, request: UpdateBodyFatMeasurementRequest): Promise<ApiResponse<BodyFatMeasurementResponse>> {
    const response = await this.api.put(`/api/admin/clients/${clientId}/body-fat-measurements/${measurementId}`, request);
    return response.data;
  }

  async deleteBodyFatMeasurement(clientId: number, measurementId: number): Promise<ApiResponse> {
    const response = await this.api.delete(`/api/admin/clients/${clientId}/body-fat-measurements/${measurementId}`);
    return response.data;
  }

  // Body Fat Measurement - Client endpoints
  async getMyBodyFatMeasurements(): Promise<ApiResponse<BodyFatMeasurementResponse[]>> {
    const response = await this.api.get('/api/client/body-fat-measurements');
    return response.data;
  }

  async getMyLatestBodyFatMeasurement(): Promise<ApiResponse<BodyFatMeasurementResponse>> {
    const response = await this.api.get('/api/client/body-fat-measurements/latest');
    return response.data;
  }
}

export const apiService = new ApiService(); 